import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useTags } from '@/hooks/use-tags';
import type { Tag, TagKind } from '@/types/tags';
import { TagBadge } from './tag-badge';

interface TagPickerProps {
  selectedIds: string[];
  onToggle: (tagId: string) => void;
  kind?: TagKind;
  triggerLabel?: string;
  showSelectedInline?: boolean;
  disabled?: boolean;
}

// Multi-select popover for choosing tags. Reads from /api/v1/tags and filters
// in-memory by the input string.
export function TagPicker({
  selectedIds,
  onToggle,
  kind = 'label',
  triggerLabel,
  showSelectedInline = false,
  disabled = false,
}: TagPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: tags = [], isLoading } = useTags({ kind });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(
      (t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q),
    );
  }, [tags, search]);

  const selectedSet = new Set(selectedIds);
  const selectedTags = showSelectedInline
    ? tags.filter((t) => selectedSet.has(t.id))
    : [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showSelectedInline &&
        selectedTags.map((tag) => (
          <TagBadge
            key={tag.id}
            tag={tag}
            onRemove={() => onToggle(tag.id)}
          />
        ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={(p) => (
            <Button {...p} variant="outline" size="sm" disabled={disabled}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              {triggerLabel ?? 'Add tag'}
            </Button>
          )}
        />
        <PopoverContent className="w-72 p-0" align="start">
          <div className="border-b p-2">
            <Input
              placeholder="Search tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {isLoading && (
              <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
            )}
            {!isLoading && filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground">No tags found.</p>
            )}
            {filtered.map((tag) => (
              <TagRow
                key={tag.id}
                tag={tag}
                selected={selectedSet.has(tag.id)}
                onToggle={() => onToggle(tag.id)}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function TagRow({
  tag,
  selected,
  onToggle,
}: {
  tag: Tag;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
    >
      <Checkbox checked={selected} />
      <TagBadge tag={tag} />
      {tag.assignment_count != null && (
        <span className="ml-auto text-xs text-muted-foreground">{tag.assignment_count}</span>
      )}
    </button>
  );
}
